var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b, _c;
import { action, computed, observable, set, toJS, makeObservable } from 'mobx';
import { default as api } from '../services/ImagePackagesApi';
import { default as notificationStore } from './NotificationStore';
class ImagePackagesStore {
    constructor(notificationStore) {
        Object.defineProperty(this, "notificationStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isSaving", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "parentImagePackages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "customImagePackages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "loadParentImagePackages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFetching = true;
                return api.getParentImagePackages()
                    .then((imagePackages) => {
                    this.parentImagePackages = imagePackages;
                })
                    .catch(this.notifyError)
                    .finally(() => {
                    this.isFetching = false;
                });
            }
        });
        Object.defineProperty(this, "loadCustomImagePackages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFetching = true;
                return api.getUserImagePackages()
                    .then((imagePackages) => {
                    this.customImagePackages = imagePackages.filter(({ parentId }) => {
                        return parentId !== null;
                    });
                })
                    .catch(this.notifyError)
                    .finally(() => {
                    this.isFetching = false;
                });
            }
        });
        Object.defineProperty(this, "saveCustomImagePackages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const promises = this.customImagePackages.map((customImagePackage) => {
                    if (customImagePackage.id !== undefined) {
                        return api.updateCustomImagePackage(customImagePackage);
                    }
                    return api.createCustomImagePackage(customImagePackage)
                        .then(({ id }) => {
                        customImagePackage.id = id;
                    });
                });
                return new Promise((resolve, reject) => {
                    Promise.all(promises)
                        .then(() => resolve())
                        .catch(this.notifyError)
                        .catch(reject);
                });
            }
        });
        Object.defineProperty(this, "deleteCustomImagePackages", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const packages = new Set(this.customImagePackages);
                const promises = this.customImagePackages
                    .filter(({ id }) => id !== undefined)
                    .map((customImagePackage) => {
                    return api.deleteCustomImagePackage(customImagePackage).then(() => {
                        packages.delete(customImagePackage);
                    });
                });
                return new Promise((resolve, reject) => {
                    Promise.all(promises)
                        .then(() => resolve())
                        .catch(this.notifyError)
                        .catch(reject)
                        .finally(() => {
                        this.customImagePackages = Array.from(packages);
                    });
                });
            }
        });
        Object.defineProperty(this, "setCustomConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (imagePackage, config) => {
                let configs;
                if (imagePackage.parentId) {
                    configs = imagePackage.imagePackageConfigs;
                }
                else {
                    const { id, title, imagePackageConfigs } = toJS(imagePackage);
                    const length = this.customImagePackages.push({
                        title,
                        imagePackageConfigs,
                        parentId: id
                    });
                    configs = this.customImagePackages[length - 1].imagePackageConfigs;
                }
                for (let i = 0; i < configs.length; i = i + 1) {
                    const { category, image_code } = configs[i];
                    if (category === config.category && image_code === config.image_code) {
                        // Post process can't be selected until mandatory field unselected, except image code gen_feature
                        if (!config.mandatory && config.image_code !== 'gen_feature') {
                            config.postProcess = false;
                        }
                        set(configs[i], config);
                        return;
                    }
                }
            }
        });
        Object.defineProperty(this, "notifyError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (reason) => {
                const { addNotification } = this.notificationStore;
                let message;
                if (reason !== undefined && Array.isArray(reason.errors)) {
                    const { errors } = reason;
                    if (errors.length > 0) {
                        const { detail, title } = errors[0];
                        if (detail !== undefined) {
                            message = detail;
                        }
                        else if (title !== undefined) {
                            message = title;
                        }
                    }
                }
                if (message === undefined) {
                    message = String(reason);
                }
                addNotification({
                    message,
                    level: 'error'
                });
                return Promise.reject(reason);
            }
        });
        makeObservable(this);
        this.notificationStore = notificationStore;
    }
    get countsByParentImagePackages() {
        return new WeakMap(this.parentImagePackages.map((imagePackage) => {
            return [
                imagePackage,
                ImagePackagesStore.getImageCounts(imagePackage)
            ];
        }));
    }
    get countsByCustomImagePackages() {
        return new WeakMap(this.customImagePackages.map((imagePackage) => {
            return [
                imagePackage,
                ImagePackagesStore.getImageCounts(imagePackage)
            ];
        }));
    }
    get imageCodesByCategories() {
        const imageCodesByCategories = new Map();
        this.parentImagePackages.forEach(({ imagePackageConfigs }) => {
            imagePackageConfigs.forEach(({ category, image_code }) => {
                if (!imageCodesByCategories.has(category)) {
                    imageCodesByCategories.set(category, new Set());
                }
                imageCodesByCategories.get(category).add(image_code);
            });
        });
        return imageCodesByCategories;
    }
}
Object.defineProperty(ImagePackagesStore, "getImageCounts", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: ({ imagePackageConfigs }) => {
        let mandatoryCount = 0;
        let postProcessCount = 0;
        imagePackageConfigs.forEach((config) => {
            if (config.mandatory)
                mandatoryCount = mandatoryCount + 1;
            if (config.postProcess)
                postProcessCount = postProcessCount + 1;
        });
        return {
            mandatoryCount,
            postProcessCount
        };
    }
});
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ImagePackagesStore.prototype, "isFetching", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ImagePackagesStore.prototype, "isSaving", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], ImagePackagesStore.prototype, "parentImagePackages", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], ImagePackagesStore.prototype, "customImagePackages", void 0);
__decorate([
    computed,
    __metadata("design:type", typeof (_a = typeof WeakMap !== "undefined" && WeakMap) === "function" ? _a : Object),
    __metadata("design:paramtypes", [])
], ImagePackagesStore.prototype, "countsByParentImagePackages", null);
__decorate([
    computed,
    __metadata("design:type", typeof (_b = typeof WeakMap !== "undefined" && WeakMap) === "function" ? _b : Object),
    __metadata("design:paramtypes", [])
], ImagePackagesStore.prototype, "countsByCustomImagePackages", null);
__decorate([
    computed,
    __metadata("design:type", typeof (_c = typeof Map !== "undefined" && Map) === "function" ? _c : Object),
    __metadata("design:paramtypes", [])
], ImagePackagesStore.prototype, "imageCodesByCategories", null);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagePackagesStore.prototype, "loadParentImagePackages", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagePackagesStore.prototype, "loadCustomImagePackages", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagePackagesStore.prototype, "saveCustomImagePackages", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagePackagesStore.prototype, "deleteCustomImagePackages", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ImagePackagesStore.prototype, "setCustomConfig", void 0);
export default new ImagePackagesStore(notificationStore);
